import _debounce from 'lodash/debounce';

export function asyncDebounce(func, wait) {
  const debounced = _debounce((resolve, reject, args) => {
    func(...args)
      .then(resolve)
      .catch(reject);
  }, wait);
  return (...args) =>
    new Promise((resolve, reject) => {
      debounced(resolve, reject, args);
    });
}

/**
 * Executes a callback function on each element of an array sequentially, ensuring that each
 * callback completes before moving to the next one. Unlike parallel execution, this function
 * ensures that async operations occur one after the other (in sequence).
 *
 * We are suppressing https://eslint.org/docs/latest/rules/no-await-in-loop
 *
 * @param {Array} arr - The array of elements to process.
 * @param {Function} callback - An async callback function that will be called with each element.
 *  The callback receives three arguments: the current element, its index, and the entire array.
 * @returns {Promise<Array<{result: any, error: boolean}>>} - A promise that resolves to an array
 *  of results where each entry contains the result of the callback or an error. Each entry is an
 *  object with the properties:
 *   - `result`: the result of the callback, or the error object if an error occurred.
 *   - `error`: a boolean indicating if an error occurred for that element.
 */
export async function asyncSequentialMap(arr, callback) {
  const results = [];

  for (let i = 0; i < arr.length; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const result = await callback(arr[i], i, arr);
      results.push({ result, error: false });
    } catch (error) {
      results.push({ result: error, error: true });
    }
  }

  return results;
}
